<template lang="pug">
.contracts-index
  .contracts-index-container
    card
      .filtering-container
        h2 {{ $t('.titles.index') }}

        .actions
          dropdown(
            :callbacks="dropDownCallbacks",
            icon="fa-download",
            orientation="left"
          )
          button.icon.button-primary(
            @click="filterOpened = !filterOpened"
            :class="{ 'opened': filterOpened }"
          )
            i.fa.fa-filter
      filter-container(
        :filter="filter"
        :opened="filterOpened"
        :i18nPath="defaultI18nScope"
      )
        manager-field(
          v-model="query.realEstateManager"
          name="real_estate_manager"
          :label="$t('.label.realEstateManager')"
          :placeholder="$t('.placeholder.realEstateManager')"
          allowClear
        )

        attendant-field(
          v-model="query.realEstateAttendant"
          name="real_estate_attendant"
          :label="$t('.label.realEstateAttendant')"
          :placeholder="$t('.placeholder.realEstateAttendant')"
          allowClear
        )

        input-field(
          v-model="query.code"
          name="code"
          :label="$t('.label.contractCode')"
          :placeholder="$t('.placeholder.contractCode')"
        )

        input-field(
          v-model="query.name"
          name="name"
          :label="$t('.label.name')"
          :placeholder="$t('.placeholder.name')"
        )

        input-field(
          v-model="query.cpf"
          name="cpf"
          :label="$t('.label.cpf')"
          :placeholder="$t('.placeholder.cpf')"
          mask="###.###.###-##"
        )

    .no-data-container(v-if="!loading && contracts.length == 0")
      img.image(src="@/assets/images/public/no-data.svg")

      h2 {{ $t('.pagination.zero') }}

    .cards-container
      loadingCard(v-if="loading" v-for="card in 6")

      contract-card(
        v-for="contract in contracts"
        :key="contract.id"
        :id="contract.id"
        :scores="contract.scores"
        :prediction="contract.result.value"
        :approved="contract.result.approved"
        :status="contract.status"
        :fetchCallback="fetch.bind(this)"
        :propertyType="contract.propertyType"
        :contractOpeningValue="contract.result.contractOpeningValue"
        :contract="contract['contract']"
        :validUntil="contract.validUntil"
      )

  report-modal(
    :show="showReportModal",
    :reportApiUrl="currentReportUrl",
    :callback="() => showReportModal = false"
  )
  confirmation-modal(
    :show="showTransfersNotFoundModal",
    :title="$t('.modals.transfersNotFoundModal.title')",
    :message="$t('.modals.transfersNotFoundModal.message')",
    @close="showTransfersNotFoundModal = false"
  )
</template>

<script>
import objects from "lib/objects"
import tool from "lib/vue-tool"
import vueAuthenticate from "lib/vue-authenticate"

import FilterContainer from "../../../components/filter-container.vue"
import ManagerField from "../manager-field.vue"
import AttendantField from "../attendant-field.vue"
import ContractCard from "./card.vue"
import LoadingCard from "../../../components/loading-card.vue"
import Dropdown from "../../../components/dropdown.vue"
import ReportModal from "../../../../../components/report-modal.vue"
import ConfirmationModal from "../../../components/confirmation-modal.vue"

export default {
  mixins: [tool, vueAuthenticate],
  components: {
    FilterContainer,
    ManagerField,
    AttendantField,
    ContractCard,
    LoadingCard,
    Dropdown,
    ReportModal,
    ConfirmationModal
  },

  mounted() {
    this.fetch()
  },

  data() {
    return {
      // i18n
      defaultI18nScope: "realEstate.contract.index",

      // data
      contracts: [],
      pagination: { total: 0 },
      loading: false,

      showReportModal: false,
      currentReportUrl: null,
      showTransfersNotFoundModal: false,

      // filter
      filterOpened: false,
      query: {
        realEstateManager: null,
        realEstateAttendant: null,
        name: null,
        cpf: null
      }
    }
  },

  props: {
    shouldFetch: { type: Boolean, default: false }
  },

  watch: {
    shouldFetch(value) {
      if (value && !this.loading && this.pagination.next) {
        this.fetch(this.pagination.next)
      }
    }
  },

  computed: {
    contractIds() {
      return this.contracts.map(({ id }) => id)
    },

    paginationText() {
      if (this.loading) return this.$t(".pagination.loading")

      return this.$t(".pagination.text", {
        length: this.contracts.length,
        total: this.pagination.total
      })
    },

    dropDownCallbacks() {
      return [
        {
          name: this.$t(".dropDown.items.transferCompositionReport"),
          callback: () => {
            this.generateReport("pendant-and-consolidated-expenses-reports")
          }
        }
      ]
    }
  },

  methods: {
    init(contracts, pagination) {
      this.contracts.push(...objects.camelize(contracts))

      this.pagination = {
        first: objects.dig(pagination, "first", "page"),
        prev: objects.dig(pagination, "prev", "page"),
        next: objects.dig(pagination, "next", "page"),
        last: objects.dig(pagination, "last", "page"),
        total: objects.dig(pagination, "total"),
      }
    },

    filter() {
      this.replaceQuery()

      this.contracts = []

      this.fetch()
      this.filterOpened = false
    },

    fetch(page = null) {
      this.loading = true

      if (page === null) this.contracts = []

      return this.$http
        .get(
          `${this.$user.prefixUrlAgency}/score-reports.json`,
          { params: this.httpParams(page) }
        )
        .then((response) => {
          const body = response.data
          const contracts = body.data
          const pagination = body.pagination || {}

          this.init(contracts, pagination)
        })
        .catch(() => {
          this.$notifications.error(this.$t(".notifications.fetch.failure"))
        })
        .finally(() => {
          this.loading = false
        })
    },

    httpParams(page = null) {
      return this.$params.toRouteParams({
        real_estate_user: this.query.realEstateManager || this.query.realEstateAttendant,
        code: this.query.code,
        name: this.query.name,
        cpf: this.query.cpf,
        sent_to_contract: true,
        page: page
      })
    },

    generateReport(path) {
      this.$http
        .get(
          `${this.$user.prefixUrlAgency}/transfers/downloads/${path}`,
          { params: this.contractIds }
        )
        .then(({ data }) => {
          const report = objects.camelize(data.data)

          this.currentReportUrl = `${this.$user.prefixUrl}/reports/${report.id}`
          this.showReportModal = true
        })
        .catch(() => {
          this.showTransfersNotFoundModal = true
        })
    }
  }
}
</script>
<style lang="scss" scoped>
.filtering-container {
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 2rem;
    font-weight: 500;
    color: $primary-color;
  }

  .actions {
    display: flex;

    button.opened {
      opacity: 0.5;
    }

    button.icon {
      font-size: 1.5rem;
      width: 4rem;
      padding: 0;
      margin-bottom: 0;
      margin-left: 1rem;
    }
  }
}

.counter-container {
  margin-bottom: 2rem;

  .counter {
    color: $grey-color-dark;
    font-style: italic;
  }
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;

  img {
    width: 31rem;
    margin-bottom: 1rem;
  }

  h2 {
    color: $primary-color;
    font-size: 3rem;
    font-weight: 400;
  }
}

.cards-container {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 3rem;
  grid-row-gap: 3rem;
  margin-bottom: 3rem;

  @media only screen and (max-width: 930px) {
    grid-template-columns: 1fr;
  }
}
</style>
